import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';
import HubPageLayout from '../components/HubPageLayout';
import HubArticleFrame from '../components/HubArticleFrame';
import AlertBody from './components/AlertBody';
import { BackToTopLink } from '../components/BackToTop';
import AgencyBanner from './components/AgencyBanner';
import AgencyNotice from './components/AgencyNotice';
import { SOURCE_LABELS } from './alertSources';
import { agencyProfile } from './agencyProfiles';
import { isEmptyHtml } from './alertBodyFormat';
import { setDashboardTitle } from '../pageTitles';
import '../HotScams/hotScamBody.css';

// Agency alert page (FBI / IC3, SSA, IRS, FTC). Since 10-28 it has the
// ScamWire page's layout: the framed header with the CyberFraud Alert / Av
// Library / Dark Psychology / Detection sub-tabs, the agency's seal-and-name
// banner in the hero (same height as the ScamWire photo so every frame is the
// same size), then text box 1, the agency's section image, text box 2, and
// the agency User Notice at the foot. Per-agency art: agencyProfiles.js.

// The frame's title (10-28 mockups): "<Source> - <alert title>", e.g.
// "FTC - Fake Rental Listings"; the agency's full name is in the banner.
const frameTitle = (alert) => {
  const source = SOURCE_LABELS[alert.source] || alert.source;
  const title = alert.title?.trim();
  return title ? `${source} - ${title}` : `${source} Alert`;
};

// The page minus data loading, shared with the admin's full-page preview.
export const AlertArticle = ({ alert, openPanel = null, onTogglePanel = () => {} }) => (
  <>
    <HubArticleFrame
      title={frameTitle(alert)}
      current={{ kind: 'alert', id: alert.id, source: alert.source }}
      openPanel={openPanel}
      onTogglePanel={onTogglePanel}
    >
      {/* Agency banner in place of a hero photo (10-28 mockups). */}
      <AgencyBanner alert={alert} />
    </HubArticleFrame>

    {/* Text box 1 - above the section image. The summary, shown only when
        there is no body, is plain text. */}
    {!isEmptyHtml(alert.full_text) ? (
      <div className="mt-10 hot-scam-body">
        <AlertBody html={alert.full_text} />
      </div>
    ) : alert.summary?.trim() ? (
      <p className="mt-10 text-black leading-relaxed">{alert.summary.trim()}</p>
    ) : null}

    <img src={agencyProfile(alert.source).sectionImage} alt="" className="w-full max-h-[340px] object-cover mt-10" />

    {/* Text box 2 - below the section image */}
    {!isEmptyHtml(alert.know_more) && (
      <div className="mt-10 hot-scam-body">
        <AlertBody html={alert.know_more} />
      </div>
    )}

    <AgencyNotice source={alert.source} />
    <BackToTopLink />
  </>
);

const FraudAlertDetailPage = () => {
  const { id } = useParams();
  const [state, setState] = useState({ alert: null, loading: true, error: false });
  const [openPanel, setOpenPanel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ alert: null, loading: true, error: false });
    setOpenPanel(null);
    fetch(`${API_BASE_URL}/bff/ic3/alerts/${id}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (!cancelled) setState({ alert: d.alert, loading: false, error: false }); })
      .catch(() => { if (!cancelled) setState({ alert: null, loading: false, error: true }); });
    return () => { cancelled = true; };
  }, [id]);

  const { alert, loading, error } = state;

  // Tab title: the alert's own title once loaded (the layout set "ScamWire").
  useEffect(() => {
    if (alert?.title) setDashboardTitle(alert.title);
  }, [alert?.title]);

  // Return to the tab the alert came from rather than the FBI/IC3 default.
  // Source is only known once the alert loads, so fall back to the hub until then.
  const backHref =
    alert?.source && alert.source !== 'ic3'
      ? `/dashboard/fraud-alerts?tab=${alert.source}`
      : '/dashboard/fraud-alerts';

  return (
    <HubPageLayout>
      <Link
        to={backHref}
        className="inline-flex items-center gap-2 mt-4 text-sm text-black hover:text-blue-600 hover:underline"
      >
        <span aria-hidden="true">&larr;</span> Back to CyberFraud Alert
      </Link>

      {loading && <p className="py-12 text-center text-gray-400">Loading alert&hellip;</p>}
      {error && (
        <div className="py-12 text-center">
          <p className="text-gray-500">Couldn&rsquo;t load this alert.</p>
          <Link to="/dashboard/fraud-alerts" className="text-blue-600 hover:underline">
            Back to CyberFraud Alert
          </Link>
        </div>
      )}

      {alert && <AlertArticle alert={alert} openPanel={openPanel} onTogglePanel={setOpenPanel} />}
    </HubPageLayout>
  );
};

export default FraudAlertDetailPage;
